import { useState, useEffect } from 'react';

const DumpList = () => {
  const [dumpList, setDumpList] = useState([]);
  const [filteredDumpList, setFilteredDumpList] = useState([]);
  const [tagFilter, setTagFilter] = useState('');

  const [text, setText] = useState('');
  const [error, setError] = useState('');

  // per-item tag inputs: { [itemId]: 'tag text' }
  const [tagInputs, setTagInputs] = useState({});
  // track which item is currently being edited
  const [editingId, setEditingId] = useState(null);
  const [editTextDrafts, setEditTextDrafts] = useState({}); // { [id]: draftText }

  // confirmation modal state for delete
  const [confirmDeleteState, setConfirmDeleteState] = useState({ show: false, item: null });

  const fetchDumpList = async () => {
    const response = await fetch('api/dump/getdumpitems');
    const data = await response.json();
    setDumpList(data || []);
    setFilteredDumpList(data || []);
  };

  useEffect(() => { fetchDumpList(); }, []);

  useEffect(() => {
    const f = (tagFilter || '').trim().toLowerCase();
    if (!f) {
      setFilteredDumpList(dumpList);
    } else {
      setFilteredDumpList(
        (dumpList || []).filter(item => {
          const textMatch = (item.text || '').toLowerCase().includes(f);
          const tagMatch = (item.tags || []).some(t => (t?.name || '').toLowerCase().includes(f));
          return textMatch || tagMatch;
        })
      );
    }
  }, [tagFilter, dumpList]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    const sanitizedText = text.trim();
    if (!sanitizedText) { setError('Input cannot be empty'); return; }

    try {
      const response = await fetch('api/dump/adddumpitem', {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(sanitizedText),
      });
      if (response.ok) {
        setText('');
        await fetchDumpList();
      } else { setError('Failed to add DumpItem'); }
    } catch { setError('Error connecting to the server'); }
  };

  const onTagInputChange = (itemId, value) => setTagInputs(prev => ({ ...prev, [itemId]: value }));

  const addTag = async (itemId) => {
    const raw = (tagInputs[itemId] || '').trim(); if (!raw) return;
    try {
      const res = await fetch(`api/dump/setdumptag?itemId=${encodeURIComponent(itemId)}&tag=${encodeURIComponent(raw)}`, { method: 'POST' });
      if (!res.ok) return; setTagInputs(prev => ({ ...prev, [itemId]: '' })); await fetchDumpList();
    } catch {}
  };

  const removeTag = async (itemId, tag) => {
    try { const res = await fetch(`api/dump/removedumptag?itemId=${encodeURIComponent(itemId)}&tag=${encodeURIComponent(tag)}`, { method: 'DELETE' });
      if (!res.ok) return; await fetchDumpList(); } catch {}
  };

  const requestDeleteItem = (item) => {
    setConfirmDeleteState({ show: true, item });
  };

  const confirmDelete = async () => {
    const item = confirmDeleteState.item;
    if (!item) { setConfirmDeleteState({ show: false, item: null }); return; }
    try {
      const res = await fetch(`api/dump/deletedumpitem/${item.id}`, { method: 'DELETE' });
      if (res.ok) {
        await fetchDumpList();
      }
    } catch {}
    finally {
      setConfirmDeleteState({ show: false, item: null });
      if (editingId === item?.id) setEditingId(null);
    }
  };

  const cancelDelete = () => setConfirmDeleteState({ show: false, item: null });

  const startEdit = (item) => {
    setEditingId(item.id);
    setEditTextDrafts(prev => ({ ...prev, [item.id]: item.text }));
  };

  const closeEdit = async (item) => {
    const draft = (editTextDrafts[item.id] || '').trim();
    const original = (item.text || '').trim();
    if (draft && draft !== original) {
      try {
        const res = await fetch(`api/dump/updatedumpitem/${item.id}`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(draft) });
        if (res.ok) {
          await fetchDumpList();
        }
      } catch {}
    }
    setEditingId(null);
  };

  const onEditTextChange = (itemId, value) => {
    setEditTextDrafts(prev => ({ ...prev, [itemId]: value }));
  };

  return (
    <div>
      <h2>Dump</h2>
      <br></br>
      <form onSubmit={handleSubmit}>
        <div>
          <input type="text" value={text} onChange={(e) => setText(e.target.value)} placeholder="New Item" />
          <button type="submit" style={{ marginLeft: 12 }}>Add</button>
        </div>
      </form>
      {error && <p style={{ color: 'red' }}>{error}</p>}

      <div style={{ marginTop: 12 }}>
        <input type="text" placeholder="Filter" value={tagFilter} onChange={(e) => setTagFilter(e.target.value)} />
      </div>
      <br></br>
      <ul className="list-unstyled" style={{ marginTop: 12, paddingLeft: 0 }}>
        {filteredDumpList.map(item => {
          const isEditing = editingId === item.id;
          const liStyle = isEditing
            ? { marginBottom: 12, padding: 12, border: '2px solid #0d6efd', borderRadius: 8, background: '#f8fbff' }
            : { marginBottom: 12, paddingBottom: 8, borderBottom: '1px solid #eee' };
          return (
            <li key={item.id} style={liStyle}>
              {!isEditing ? (
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <strong>{item.text}</strong>
                    {' '}
                    <span style={{ color: '#666' }}>
                      {(item.tags || []).map(t => t.name).join(', ')}
                    </span>
                  </div>
                  <div>
                    <button className="btn btn-sm btn-outline-primary" onClick={() => startEdit(item)}>Edit</button>
                  </div>
                </div>
              ) : (
                <div>
                  <div className="mb-2">
                    <label className="form-label">Text</label>
                    <div className="d-flex gap-2">
                      <input type="text" className="form-control" value={editTextDrafts[item.id] || ''} onChange={e => onEditTextChange(item.id, e.target.value)} />
                    </div>
                  </div>

                  <div className="mb-2">
                    <label className="form-label">Tags</label>
                    <div style={{ marginBottom: 6 }}>
                      {(item.tags || []).map(t => (
                        <span key={t.id} className="badge bg-light text-dark me-2">
                          {t.name}
                          <button type="button" className="btn btn-sm btn-link text-danger ms-1 p-0" title="Remove tag" onClick={() => removeTag(item.id, t.name)}>x</button>
                        </span>
                      ))}
                    </div>
                    <div className="d-flex align-items-center">
                      <input type="text" className="form-control" placeholder="New Tag" value={tagInputs[item.id] || ''} onChange={(e) => onTagInputChange(item.id, e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addTag(item.id); } }} />
                      <button className="btn btn-sm btn-outline-primary ms-2" onClick={() => addTag(item.id)}>Add</button>
                    </div>
                  </div>
                  <div className="d-flex gap-2">
                      <button className="btn btn-outline-danger  ms-auto" onClick={() => requestDeleteItem(item)}>Delete item</button>
                    <button className="btn btn-secondary" onClick={() => closeEdit(item)}>Close</button>
                  </div>
                </div>
              )}
            </li>
          );
        })}
      </ul>

      {confirmDeleteState.show && (
        <>
          <div className="modal fade show d-block" tabIndex="-1" role="dialog" aria-modal="true">
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Delete item</h5>
                  <button type="button" className="btn-close" aria-label="Close" onClick={cancelDelete}></button>
                </div>
                <div className="modal-body">
                  <p>Are you sure you want to delete this item?</p>
                  <div className="small text-muted">{confirmDeleteState.item?.text}</div>
                </div>
                <div className="modal-footer">
                  <button className="btn btn-secondary" onClick={cancelDelete}>Cancel</button>
                  <button className="btn btn-danger" onClick={confirmDelete}>Delete</button>
                </div>
              </div>
            </div>
          </div>
          <div className="modal-backdrop fade show"></div>
        </>
      )}
    </div>
  );
};

export default DumpList;